import type { StorySqlChart, StorySqlTextItem } from "../StorySqlDialog";

/** The slice of a canvas widget the picker needs. */
export interface SqlChartCandidate {
  id: string;
  type: string;
  title?: string | null;
}

const SQL_CHART_TYPES: ReadonlySet<string> = new Set(["chart", "kpi"]);

/** The editable charts and KPIs, in canvas order, named by their title or else by their place. */
export const toSqlChartOptions = (
  widgets: readonly SqlChartCandidate[],
  editableIds: ReadonlySet<string>,
  fallbackLabel: string,
  kindLabels: Readonly<Record<string, string>> = {},
  /** Multi-page Stories: each chart's page and tags, for the dialog's "Group by page" and "Tagged by". */
  placement?: { pageOfItem: Readonly<Record<string, string | null>>; tagsOfItem: Readonly<Record<string, string[]>> },
): StorySqlChart[] =>
  widgets
    .filter(
      (widget) =>
        SQL_CHART_TYPES.has(widget.type) && editableIds.has(widget.id),
    )
    .map((widget, index) => ({
      id: widget.id,
      label: widget.title?.trim() || `${fallbackLabel} ${index + 1}`,
      kind: kindLabels[widget.type],
      ...(placement ? { pageId: placement.pageOfItem[widget.id] ?? null, tags: placement.tagsOfItem[widget.id] ?? [] } : {}),
    }));

/** The text tiles, in canvas order, named by their title or else by their place ("Text 2"). */
export const toSqlTextItems = (
  widgets: readonly { id: string; type: string; title?: unknown }[],
  fallbackLabel: string,
): StorySqlTextItem[] =>
  widgets
    .filter((widget) => widget.type === "text")
    .map((widget, index) => ({
      id: widget.id,
      label: (typeof widget.title === "string" && widget.title.trim()) || `${fallbackLabel} ${index + 1}`,
    }));

const STORY_SUMMARY_TYPES = ["summary", "narrative"] as const;

/** Markdown emphasis and heading marks dropped, whitespace collapsed: a plain sentence for a message. */
const toPlainText = (body: string): string =>
  body.replace(/[*_`#>]+/g, "").replace(/\s+/g, " ").trim();

/**
 * The story-level summary text: the first story `summary` tile's body, else the first story `narrative`
 * tile's (a chart's own narrative row is excluded by `isChartRow`). Null when the story has none.
 */
export const toStorySummaryText = (
  widgets: readonly { id: string; type: string; body?: unknown }[],
  isChartRow: (id: string) => boolean = () => false,
): string | null => {
  for (const type of STORY_SUMMARY_TYPES) {
    const match = widgets.find(
      (widget) =>
        widget.type === type &&
        !isChartRow(widget.id) &&
        typeof widget.body === "string" &&
        toPlainText(widget.body).length > 0,
    );
    if (match) return toPlainText(match.body as string);
  }
  return null;
};

/** The chart the dialog opens on: the one in the editor, else the first. Null with no charts. */
export const pickSqlChartId = (
  options: readonly StorySqlChart[],
  openEditorItemId?: string | null,
): string | null => {
  if (
    openEditorItemId &&
    options.some((option) => option.id === openEditorItemId)
  )
    return openEditorItemId;
  return options[0]?.id ?? null;
};

export { toDrillPath, toFilterSections } from "../StoryCanvasFilterStrip/storyCanvasFilterStripMappers";
export type { DrillTrailStep, FilterSectionsInput } from "../StoryCanvasFilterStrip/storyCanvasFilterStripMappers";
